import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, session } from "../api.js";
import { useCms, useCmsStatus } from "../cms/store.js";

/*
 * Blokujący modal ponownej akceptacji Regulaminu/Polityki prywatności —
 * pojawia się, gdy zalogowany użytkownik (dowolna rola) ma zaakceptowaną
 * wersję starszą niż aktualna w CMS. Celowo NIE reużywa components/ui.jsx
 * Modal — ten modal nie zamyka się przez tło, Escape ani X, bo to nie jest
 * zwykłe okno, tylko wymóg złożenia zgody.
 *
 * `onActiveChange(bool)` informuje App.js, żeby oznaczył resztę aplikacji
 * jako `inert` — bez tego dało się klawiaturą (Tab) dotrzeć do przycisków
 * pod nakładką, mimo że wizualnie zasłonięte.
 */
export default function LegalReacceptGate({ onActiveChange }) {
  const { legal } = useCms();
  const cmsStatus = useCmsStatus();
  const [me, setMe] = useState(session.get()?.user || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const onChange = () => setMe(session.get()?.user || null);
    window.addEventListener("prawly:session-expired", onChange);
    return () => window.removeEventListener("prawly:session-expired", onChange);
  }, []);

  /* Dopóki treść z serwera nie wczyta się choć raz w tej sesji przeglądarki
     (cmsStatus === "ready"), `legal.*.updated` może pochodzić z wbudowanej
     treści fabrycznej (cms/seed.js) — ta nie jest na bieżąco synchronizowana
     z tym, co admin zapisał w bazie, więc jej data nieraz różni się od
     realnie zaakceptowanej. Przy problemach z serwerem (status "loading"
     na starcie, "error" po nieudanym odświeżeniu) to dawało fałszywy alarm
     „zaakceptuj ponownie", mimo że treść realnie się nie zmieniła — dlatego
     sprawdzamy nieaktualność wyłącznie na podstawie treści faktycznie
     potwierdzonej przez serwer. */
  const trustedLegal = cmsStatus === "ready";
  const termsStale =
    trustedLegal &&
    !!me &&
    !!legal?.regulamin?.updated &&
    me.terms_version_accepted !== legal.regulamin.updated;
  const privacyStale =
    trustedLegal &&
    !!me &&
    !!legal?.prywatnosc?.updated &&
    me.privacy_version_accepted !== legal.prywatnosc.updated;
  const active = termsStale || privacyStale;

  useEffect(() => {
    onActiveChange?.(active);
  }, [active, onActiveChange]);

  if (!active) return null;

  const which = termsStale && privacyStale ? "both" : termsStale ? "terms" : "privacy";
  const docsLabel =
    termsStale && privacyStale
      ? "Regulamin i Politykę prywatności"
      : termsStale
      ? "Regulamin"
      : "Politykę prywatności";

  const accept = async () => {
    setBusy(true);
    setErr("");
    try {
      const { user } = await api("/accept-legal-docs", { method: "POST", body: { which } });
      session.set({ ...session.get(), user });
      setMe(user);
    } catch (ex) {
      setErr(ex.message);
    }
    setBusy(false);
  };

  return (
    <div className="modal-veil" style={{ zIndex: 95 }}>
      <div
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Aktualizacja dokumentów prawnych"
      >
        <div className="modal-head">
          <b>Zaktualizowaliśmy {docsLabel}</b>
        </div>
        <div className="modal-body">
          <p>
            Zanim będzie można dalej korzystać z konta, prosimy o zapoznanie się z nową
            treścią
            {termsStale && (
              <>
                {" "}
                —{" "}
                <Link to="/regulamin" target="_blank" rel="noreferrer">
                  Regulamin
                </Link>
              </>
            )}
            {termsStale && privacyStale && " oraz "}
            {privacyStale && (
              <>
                {!termsStale && " — "}
                <Link to="/polityka-prywatnosci" target="_blank" rel="noreferrer">
                  Politykę prywatności
                </Link>
              </>
            )}
            {" "}— i potwierdzenie akceptacji poniżej.
          </p>
          {err && <p className="error">{err}</p>}
          <button className="btn wax" onClick={accept} disabled={busy}>
            {busy ? "Zapisywanie…" : "Rozumiem, akceptuję"}
          </button>
        </div>
      </div>
    </div>
  );
}
